import { forwardRef, useEffect, useRef, useState } from "react";
import type { PriceLevelInfo } from "./BottomUpDailyGrid";
import { PLAN_PRODUCT_COL_WIDTH } from "@/lib/gridLayout";

const SUM_COL_WIDTH = 120;
const DAY_COL_WIDTH = 60;
// Slim band above the date row where promo labels span their date range.
// Always rendered so the grid height doesn't shift between months.
const LABEL_BAND_H = 22;

function shortDayLabel(iso: string): string {
  const [, , d] = iso.split("-");
  return parseInt(d, 10).toString();
}

function weekdayLabel(iso: string): string {
  const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const d = new Date(iso + "T00:00:00");
  return labels[d.getDay()];
}

interface Props {
  days: string[];
  pricingByDate: Record<string, string>;
  pricingLabelsByDate?: Record<string, string>;
  priceLevelById: Map<string, PriceLevelInfo>;
  selectedDates?: Set<string>;
  onToggleDate?: (date: string, shiftKey: boolean) => void;
  onDragSelect?: (dates: string[]) => void;
  // Elapsed days: not selectable as promo days.
  isLocked?: (iso: string) => boolean;
  // Sticky top of the first header row: the height of the toolbar stuck
  // above the grid, so the two rows stack under it.
  top?: number;
}

const DailyGridHeader = forwardRef<HTMLTableSectionElement, Props>(function DailyGridHeader(
  {
    days,
    pricingByDate,
    pricingLabelsByDate = {},
    priceLevelById,
    selectedDates,
    onToggleDate,
    onDragSelect,
    isLocked = () => false,
    top = 0,
  },
  ref,
) {
  // Spreadsheet-style drag selection across date headers: mousedown anchors,
  // dragging previews a continuous range, mouseup commits it. A mousedown
  // without movement falls back to the click behavior (toggle / shift-extend).
  const [dragAnchor, setDragAnchor] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);
  const dragShift = useRef(false);
  const dragMoved = useRef(false);

  useEffect(() => {
    if (!dragAnchor) return;
    const commit = () => {
      if (dragMoved.current && dragEnd && onDragSelect) {
        const a = days.indexOf(dragAnchor);
        const b = days.indexOf(dragEnd);
        if (a !== -1 && b !== -1) onDragSelect(days.slice(Math.min(a, b), Math.max(a, b) + 1));
      } else {
        onToggleDate?.(dragAnchor, dragShift.current);
      }
      setDragAnchor(null);
      setDragEnd(null);
    };
    window.addEventListener("mouseup", commit);
    return () => window.removeEventListener("mouseup", commit);
  }, [dragAnchor, dragEnd, days, onDragSelect, onToggleDate]);

  const dragRange = (() => {
    if (!dragAnchor || !dragEnd || !dragMoved.current) return null;
    const a = days.indexOf(dragAnchor);
    const b = days.indexOf(dragEnd);
    if (a === -1 || b === -1) return null;
    return [Math.min(a, b), Math.max(a, b)] as const;
  })();

  // Consecutive days sharing the same label + level render one spanning band.
  const bands = (() => {
    const out: { start: number; len: number; label?: string; color?: string | null }[] = [];
    for (let i = 0; i < days.length; ) {
      const label = pricingLabelsByDate[days[i]];
      const levelId = pricingByDate[days[i]];
      if (!label) {
        out.push({ start: i, len: 1 });
        i += 1;
        continue;
      }
      let j = i + 1;
      while (j < days.length && pricingLabelsByDate[days[j]] === label && pricingByDate[days[j]] === levelId) j += 1;
      out.push({ start: i, len: j - i, label, color: levelId ? priceLevelById.get(levelId)?.color : null });
      i = j;
    }
    return out;
  })();

  return (
    <thead ref={ref}>
      <tr style={{ height: LABEL_BAND_H }}>
        <th
          rowSpan={2}
          style={{ background: "var(--muted)", color: "var(--muted-foreground)", borderColor: "var(--border)", width: PLAN_PRODUCT_COL_WIDTH, top }}
          className="sticky left-0 z-30 text-left px-3 py-2 font-semibold whitespace-nowrap border-r"
        >
          Product
        </th>
        {bands.map((b) => (
          <th
            key={b.start}
            colSpan={b.len}
            style={{ background: "var(--muted)", top }}
            className="sticky z-20 p-0 font-normal"
          >
            {b.label && (
              <div
                title={b.label}
                style={{
                  background: b.color ? `color-mix(in srgb, ${b.color} 25%, transparent)` : "var(--accent)",
                  color: "var(--foreground)",
                }}
                className="mx-0.5 mt-1 rounded-sm px-1 text-[9px] font-semibold uppercase tracking-wider truncate text-center leading-4"
              >
                {b.label}
              </div>
            )}
          </th>
        ))}
        <th
          rowSpan={2}
          style={{ background: "var(--muted)", color: "var(--muted-foreground)", width: SUM_COL_WIDTH, top }}
          className="sticky z-20 px-2 py-2 text-right font-semibold whitespace-nowrap align-bottom"
        >
          Sum vs Target
        </th>
      </tr>
      <tr style={{ borderColor: "var(--border)" }} className="border-b">
        {days.map((d, i) => {
          const levelId = pricingByDate[d];
          const level = levelId ? priceLevelById.get(levelId) : null;
          const label = pricingLabelsByDate[d];
          // Union, not replacement: existing selections keep their rings while
          // a new range is being dragged — only the new days light up.
          const locked = isLocked(d);
          const selectable = Boolean(onToggleDate) && !locked;
          const selected =
            !locked &&
            ((selectedDates?.has(d) ?? false) ||
              (dragRange !== null && i >= dragRange[0] && i <= dragRange[1]));
          return (
            <th
              key={d}
              style={{
                background: selected ? "var(--accent)" : "var(--muted)",
                color: "var(--muted-foreground)",
                width: DAY_COL_WIDTH,
                top: top + LABEL_BAND_H,
                boxShadow: selected ? "inset 0 0 0 1.5px var(--ring)" : undefined,
              }}
              className={`sticky z-20 px-1.5 pt-0 pb-2 text-right font-semibold whitespace-nowrap${selectable ? " cursor-pointer select-none" : ""}`}
              title={level ? [level.name, label].filter(Boolean).join(" — ") : d}
              onMouseDown={
                selectable
                  ? (e) => {
                      e.preventDefault();
                      dragShift.current = e.shiftKey;
                      dragMoved.current = false;
                      setDragAnchor(d);
                      setDragEnd(d);
                    }
                  : undefined
              }
              onMouseEnter={
                onToggleDate
                  ? () => {
                      if (dragAnchor) {
                        if (d !== dragAnchor) dragMoved.current = true;
                        setDragEnd(d);
                      }
                    }
                  : undefined
              }
            >
              <div
                style={{
                  height: 4,
                  marginLeft: -6,
                  marginRight: -6,
                  marginBottom: 6,
                  background: level?.color || "transparent",
                }}
              />
              <div>{shortDayLabel(d)}</div>
              <div className="text-[9px] opacity-70">{weekdayLabel(d)}</div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
});

export default DailyGridHeader;
